import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { BLOG_ARTICLES, BlogSection } from '@/mocks/blog';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { useArticleViews, formatViews } from '@/hooks/useArticleViews';
import TopArticles from './components/TopArticles';
import ShareWidget from './components/ShareWidget';
import ReadProgressRing from './components/ReadProgressRing';
import ArticleLikeWidget from './components/ArticleLikeWidget';

const ACCENT = '#ff2d78';

const CATEGORY_COLORS: Record<string, string> = {
  'Иммунитет': '#ff2d78',
  'Уход за кожей': '#ff6b00',
  'Витамины и БАД': '#00f5ff',
  'Здоровый сон': '#39ff14',
  'Простуда и грипп': '#f0ff00',
  'Сердце и сосуды': '#ff2d78',
  'Пищеварение': '#39ff14',
  'Детское здоровье': '#00f5ff',
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

/* ── Content block renderer ── */
const ContentBlock = ({ section, index }: { section: BlogSection; index: number }) => {
  switch (section.type) {
    case 'heading':
      return (
        <h2
          id={`section-${index}`}
          className="font-orbitron font-bold text-lg md:text-xl text-white mt-10 mb-4 flex items-center gap-3"
        >
          <span
            className="w-1 h-6 rounded-full shrink-0"
            style={{ background: `linear-gradient(to bottom, ${ACCENT}, transparent)` }}
          />
          {section.text}
        </h2>
      );
    case 'paragraph':
      return (
        <p className="font-mono text-sm leading-[1.9] text-white/60">
          {section.text}
        </p>
      );
    case 'list':
      return (
        <ul className="space-y-2.5">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-3 font-mono text-sm text-white/60">
              <span
                className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: ACCENT, boxShadow: `0 0 5px ${ACCENT}` }}
              />
              {item}
            </li>
          ))}
        </ul>
      );
    case 'tip':
      return (
        <div
          className="flex gap-4 p-5 rounded-xl"
          style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.18)' }}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.25)' }}>
            <i className="ri-lightbulb-flash-line" style={{ color: '#39ff14', fontSize: '16px' }} />
          </div>
          <div>
            <p className="font-orbitron font-bold text-xs mb-1.5" style={{ color: '#39ff14' }}>Совет фармацевта</p>
            <p className="font-mono text-sm leading-relaxed text-white/60">{section.text}</p>
          </div>
        </div>
      );
    case 'warning':
      return (
        <div
          className="flex gap-4 p-5 rounded-xl"
          style={{ background: 'rgba(255,107,0,0.05)', border: '1px solid rgba(255,107,0,0.22)' }}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.25)' }}>
            <i className="ri-alert-line" style={{ color: '#ff6b00', fontSize: '16px' }} />
          </div>
          <div>
            <p className="font-orbitron font-bold text-xs mb-1.5" style={{ color: '#ff6b00' }}>Важно знать</p>
            <p className="font-mono text-sm leading-relaxed text-white/60">{section.text}</p>
          </div>
        </div>
      );
    case 'quote':
      return (
        <blockquote
          className="relative px-6 py-5 rounded-xl"
          style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.15)', borderLeft: '3px solid #00f5ff' }}
        >
          <i className="ri-double-quotes-l absolute top-3 right-4 text-3xl opacity-10" style={{ color: '#00f5ff' }} />
          <p className="font-mono text-sm leading-relaxed italic" style={{ color: 'rgba(0,245,255,0.75)' }}>
            {section.text}
          </p>
        </blockquote>
      );
    default:
      return null;
  }
};



/* ── Article rating ── */
const ArticleRating = ({ color }: { color: string }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleRate = (r: number) => {
    setRating(r);
    setTimeout(() => setSubmitted(true), 300);
  };

  return (
    <div
      className="p-6 rounded-2xl text-center"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {!submitted ? (
        <>
          <p className="font-orbitron font-bold text-sm text-white mb-1">Была ли статья полезной?</p>
          <p className="font-mono text-xs text-white/35 mb-5">Оцените материал</p>
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4, 5].map(r => (
              <button
                key={r}
                onClick={() => handleRate(r)}
                onMouseEnter={() => setHovered(r)}
                onMouseLeave={() => setHovered(null)}
                className="cursor-pointer transition-all duration-150"
                style={{ transform: (hovered ?? 0) >= r ? 'scale(1.2)' : 'scale(1)' }}
              >
                <i
                  className={`text-2xl ${(hovered ?? rating ?? 0) >= r ? 'ri-star-fill' : 'ri-star-line'}`}
                  style={{ color: (hovered ?? rating ?? 0) >= r ? '#f0ff00' : 'rgba(255,255,255,0.2)' }}
                />
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-12 h-12 flex items-center justify-center rounded-full"
            style={{ background: `${color}15`, border: `2px solid ${color}` }}
          >
            <i className="ri-check-line text-xl" style={{ color }} />
          </div>
          <p className="font-orbitron font-bold text-sm text-white">Спасибо за оценку!</p>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(r => (
              <i key={r} className={`text-sm ${(rating ?? 0) >= r ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Author card ── */
const AuthorCard = ({ author, role, color }: { author: string; role: string; color: string }) => (
  <div
    className="p-5 rounded-2xl flex items-start gap-4"
    style={{ background: `${color}06`, border: `1px solid ${color}20` }}
  >
    <div
      className="w-14 h-14 rounded-xl flex items-center justify-center font-orbitron font-black text-xl shrink-0"
      style={{ background: `${color}18`, border: `2px solid ${color}40`, color }}
    >
      {author.charAt(0)}
    </div>
    <div>
      <div className="flex items-center gap-2 mb-0.5">
        <span className="font-orbitron font-bold text-sm text-white">{author}</span>
        <span
          className="px-2 py-0.5 rounded-full font-mono text-[9px]"
          style={{ background: 'rgba(0,245,255,0.1)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.2)' }}
        >
          <i className="ri-shield-check-fill mr-0.5" />
          Эксперт
        </span>
      </div>
      <p className="font-mono text-xs text-white/40 mb-3">{role}</p>
      <p className="font-mono text-xs text-white/30 leading-relaxed">
        Автор более 40 научных публикаций. Специализируется на доказательной медицине и профилактике заболеваний.
      </p>
    </div>
  </div>
);

/* ── Main page ── */
const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [readProgress, setReadProgress] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const viewCount = useArticleViews(slug);

  const article = BLOG_ARTICLES.find(a => a.slug === slug);
  const related = article ? BLOG_ARTICLES.filter(a => article.relatedIds.includes(a.id)) : [];
  const color = article ? (CATEGORY_COLORS[article.category] ?? ACCENT) : ACCENT;

  // Extract headings for TOC
  const headings = article
    ? article.content
        .map((s, i) => s.type === 'heading' ? { id: `section-${i}`, text: s.text ?? '' } : null)
        .filter(Boolean) as { id: string; text: string }[]
    : [];

  useEffect(() => {
    if (!article) navigate('/blog');
  }, [article, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Read progress
  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setReadProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active heading tracking
  const handleScroll = useCallback(() => {
    if (!headings.length) return;
    const offsets = headings.map(h => {
      const el = document.getElementById(h.id);
      return el ? { id: h.id, top: el.getBoundingClientRect().top } : null;
    }).filter(Boolean) as { id: string; top: number }[];

    const active = offsets.filter(o => o.top <= 120).pop();
    if (active) setActiveHeadingId(active.id);
    else if (offsets.length) setActiveHeadingId('');
  }, [headings]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (!article) return null;

  // Estimate word count from content
  const wordCount = article.content.reduce((acc, s) => {
    const text = s.text ?? (s.items?.join(' ') ?? '');
    return acc + text.split(/\s+/).filter(Boolean).length;
  }, 0) + article.excerpt.split(/\s+/).length;

  const articleUrl = `${SITE_URL}/blog/${article.slug}`;

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      headline: article.title,
      name: article.title,
      description: article.excerpt,
      image: {
        '@type': 'ImageObject',
        url: article.image,
        width: 800,
        height: 500,
      },
      author: {
        '@type': 'Person',
        name: article.author,
        jobTitle: article.authorRole,
        worksFor: { '@type': 'Organization', name: 'NeonPharma', url: SITE_URL },
      },
      publisher: {
        '@type': 'Organization',
        name: 'NeonPharma',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
      },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      url: articleUrl,
      keywords: article.tags.join(', '),
      articleSection: article.category,
      inLanguage: 'ru-RU',
      wordCount,
      timeRequired: `PT${article.readTime}M`,
      about: { '@type': 'MedicalCondition', name: article.category },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.article-lead', '.article-body h2'],
      },
      medicalAudience: { '@type': 'MedicalAudience', audienceType: 'Patient' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Блог', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: article.category, item: `${SITE_URL}/blog?category=${article.categorySlug}` },
        { '@type': 'ListItem', position: 4, name: article.title, item: articleUrl },
      ],
    },
    ...(related.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Читайте также',
      numberOfItems: related.length,
      itemListElement: related.map((r, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: r.title,
        url: `${SITE_URL}/blog/${r.slug}`,
      })),
    }] : []),
  ];

  return (
    <>
      <SeoHead
        title={`${article.title} | Блог NeonPharma`}
        description={article.excerpt}
        keywords={article.tags.join(', ')}
        canonical={`/blog/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        jsonLd={articleJsonLd}
      />

      {/* Read progress bar */}
      <div
        className="fixed top-0 left-0 z-[60] h-0.5 transition-all duration-100"
        style={{ width: `${readProgress}%`, background: `linear-gradient(90deg, ${color}, #00f5ff)` }}
        role="progressbar"
        aria-valuenow={Math.round(readProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Прогресс чтения статьи"
      />

      <div className="min-h-screen" style={{ background: '#05050a' }}>
        <Navbar />

        {/* ── Hero ── */}
        <div className="relative pt-[72px]" style={{ height: '460px' }}>
          <img
            src={article.image}
            alt={`${article.title} — статья блога NeonPharma`}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, rgba(5,5,10,0.25) 0%, rgba(5,5,10,0.92) 100%)' }}
          />

          {/* Hero content */}
          <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-12 pb-10">
            <div className="max-w-7xl mx-auto">
              {/* Breadcrumb */}
              <nav aria-label="Хлебные крошки" className="flex items-center gap-2 font-mono text-xs text-white/35 mb-5 flex-wrap">
                <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
                <i className="ri-arrow-right-s-line" />
                <Link to="/blog" className="hover:text-white/60 transition-colors">Блог</Link>
                <i className="ri-arrow-right-s-line" />
                <Link
                  to={`/blog?category=${article.categorySlug}`}
                  className="hover:text-white/60 transition-colors"
                  style={{ color: `${color}cc` }}
                >
                  {article.category}
                </Link>
                <i className="ri-arrow-right-s-line" />
                <span className="text-white/50 line-clamp-1 max-w-[200px]">{article.title}</span>
              </nav>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span
                  className="px-3 py-1 rounded-full font-mono text-xs font-bold"
                  style={{ background: color, color: '#050508', boxShadow: `0 0 12px ${color}70` }}
                >
                  {article.category}
                </span>
                <span className="flex items-center gap-1.5 font-mono text-xs text-white/50">
                  <i className="ri-time-line" />
                  {article.readTime} мин чтения
                </span>
                <span className="flex items-center gap-1.5 font-mono text-xs text-white/50">
                  <i className="ri-calendar-line" />
                  <time dateTime={article.date}>{formatDate(article.date)}</time>
                </span>
                <span className="flex items-center gap-1.5 font-mono text-xs text-white/50">
                  <i className="ri-file-word-line" />
                  ~{wordCount} слов
                </span>
                {viewCount > 0 && (
                  <span className="flex items-center gap-1.5 font-mono text-xs" style={{ color: 'rgba(255,45,120,0.7)' }}>
                    <i className="ri-eye-line" />
                    {formatViews(viewCount)} просмотров
                  </span>
                )}
              </div>

              <h1
                className="font-orbitron font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
              >
                {article.title}
              </h1>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-6 lg:px-12 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">

              {/* ── Main content ── */}
              <article className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
                <meta itemProp="headline" content={article.title} />
                <meta itemProp="datePublished" content={article.date} />
                <meta itemProp="author" content={article.author} />
                <meta itemProp="image" content={article.image} />

                {/* Author + meta */}
                <header
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center font-orbitron font-bold text-base"
                      style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
                      aria-hidden="true"
                    >
                      {article.author.charAt(0)}
                    </div>
                    <div>
                      <p className="font-mono text-sm text-white" itemProp="author">{article.author}</p>
                      <p className="font-mono text-xs text-white/35">{article.authorRole}</p>
                    </div>
                  </div>

                  {/* Share buttons */}
                  <div className="flex items-center gap-2" aria-label="Поделиться статьёй">
                    {[
                      { icon: 'ri-telegram-line', label: 'Поделиться в Telegram', href: `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}`, c: '#2AABEE' },
                      { icon: 'ri-vk-line', label: 'Поделиться ВКонтакте', href: `https://vk.com/share.php?url=${encodeURIComponent(window.location.href)}`, c: '#4C75A3' },
                    ].map(s => (
                      <a
                        key={s.icon}
                        href={s.href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        aria-label={s.label}
                        className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
                        style={{ background: `${s.c}12`, border: `1px solid ${s.c}25`, color: s.c }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${s.c}22`; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${s.c}12`; }}
                      >
                        <i className={s.icon} style={{ fontSize: '14px' }} />
                      </a>
                    ))}
                    <button
                      onClick={handleCopy}
                      aria-label={copied ? 'Ссылка скопирована' : 'Скопировать ссылку на статью'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                      style={copied
                        ? { background: 'rgba(57,255,20,0.12)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.3)' }
                        : { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }
                      }
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-links-line'} />
                      {copied ? 'Скопировано' : 'Ссылка'}
                    </button>
                  </div>
                </header>

                {/* Lead paragraph */}
                <p
                  className="article-lead font-mono text-base leading-relaxed mb-8 pl-5"
                  style={{ color: 'rgba(255,255,255,0.65)', borderLeft: `3px solid ${color}` }}
                  itemProp="description"
                >
                  {article.excerpt}
                </p>

                {/* Article body */}
                <div ref={contentRef} className="article-body space-y-5" itemProp="articleBody">
                  {article.content.map((section, idx) => (
                    <ContentBlock key={idx} section={section} index={idx} />
                  ))}
                </div>

                {/* Tags */}
                <footer
                  className="flex flex-wrap gap-2 mt-12 pt-8"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <span className="font-mono text-xs text-white/25 self-center">Теги:</span>
                  {article.tags.map(tag => (
                    <Link
                      key={tag}
                      to={`/blog?tag=${tag}`}
                      rel="tag"
                      className="px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                      style={{ background: 'rgba(0,245,255,0.06)', color: 'rgba(0,245,255,0.55)', border: '1px solid rgba(0,245,255,0.12)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.12)'; (e.currentTarget as HTMLElement).style.color = '#00f5ff'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(0,245,255,0.55)'; }}
                    >
                      #{tag}
                    </Link>
                  ))}
                </footer>

                {/* Author card */}
                <div className="mt-8">
                  <AuthorCard author={article.author} role={article.authorRole} color={color} />
                </div>

                {/* Rating */}
                <div className="mt-6">
                  <ArticleRating color={color} />
                </div>

                {/* Disclaimer */}
                <div
                  className="mt-6 p-4 rounded-xl font-mono text-xs leading-relaxed"
                  style={{ background: 'rgba(255,45,120,0.04)', border: '1px solid rgba(255,45,120,0.12)', color: 'rgba(255,255,255,0.3)' }}
                  role="note"
                  aria-label="Медицинский дисклеймер"
                >
                  <i className="ri-information-line mr-2" style={{ color: ACCENT }} />
                  Статья носит информационный характер. Перед применением любых препаратов проконсультируйтесь с врачом. Имеются противопоказания.
                </div>

                {/* Navigation between articles */}
                <nav aria-label="Навигация по статьям" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                  {(() => {
                    const idx = BLOG_ARTICLES.findIndex(a => a.slug === slug);
                    const prev = idx > 0 ? BLOG_ARTICLES[idx - 1] : null;
                    const next = idx < BLOG_ARTICLES.length - 1 ? BLOG_ARTICLES[idx + 1] : null;
                    return (
                      <>
                        {prev ? (
                          <Link
                            to={`/blog/${prev.slug}`}
                            rel="prev"
                            className="flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 group"
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}30`; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                          >
                            <i className="ri-arrow-left-line text-white/30 group-hover:text-white/60 transition-colors shrink-0" />
                            <div className="min-w-0">
                              <p className="font-mono text-[10px] text-white/25 mb-0.5">Предыдущая</p>
                              <p className="font-orbitron font-bold text-xs text-white/60 line-clamp-1 group-hover:text-white/80 transition-colors">{prev.title}</p>
                            </div>
                          </Link>
                        ) : <div />}
                        {next ? (
                          <Link
                            to={`/blog/${next.slug}`}
                            rel="next"
                            className="flex items-center justify-end gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 group text-right"
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}30`; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                          >
                            <div className="min-w-0">
                              <p className="font-mono text-[10px] text-white/25 mb-0.5">Следующая</p>
                              <p className="font-orbitron font-bold text-xs text-white/60 line-clamp-1 group-hover:text-white/80 transition-colors">{next.title}</p>
                            </div>
                            <i className="ri-arrow-right-line text-white/30 group-hover:text-white/60 transition-colors shrink-0" />
                          </Link>
                        ) : <div />}
                      </>
                    );
                  })()}
                </nav>
              </article>

              {/* ── Sidebar ── */}
              <aside className="w-full lg:w-64 shrink-0" aria-label="Боковая панель статьи">
                <div className="sticky top-24 flex flex-col gap-5 max-h-[calc(100vh-7rem)] overflow-y-auto pr-1 scrollbar-thin">

                  {/* Table of contents */}
                  {headings.length > 0 && (
                    <div
                      className="rounded-2xl p-5"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(to bottom, ${ACCENT}, transparent)` }} />
                          <span className="font-orbitron font-bold text-xs text-white">Содержание</span>
                        </div>
                        <ReadProgressRing progress={readProgress} color={color} size={28} strokeWidth={2.5} />
                      </div>
                      <nav className="space-y-1">
                        {headings.map(h => {
                          const isActive = activeHeadingId === h.id;
                          return (
                            <a
                              key={h.id}
                              href={`#${h.id}`}
                              onClick={e => {
                                e.preventDefault();
                                document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              className="flex items-start gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 group"
                              style={{
                                background: isActive ? `${ACCENT}12` : 'transparent',
                                border: isActive ? `1px solid ${ACCENT}25` : '1px solid transparent',
                              }}
                            >
                              <span
                                className="w-1 h-1 rounded-full mt-1.5 shrink-0 transition-all duration-200"
                                style={{ background: isActive ? ACCENT : 'rgba(255,255,255,0.2)', boxShadow: isActive ? `0 0 5px ${ACCENT}` : 'none' }}
                              />
                              <span
                                className="font-mono text-xs leading-snug transition-colors duration-200"
                                style={{ color: isActive ? ACCENT : 'rgba(255,255,255,0.4)' }}
                              >
                                {h.text}
                              </span>
                            </a>
                          );
                        })}
                      </nav>
                    </div>
                  )}

                  {/* Article info */}
                  <div
                    className="rounded-2xl p-5"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-5 rounded-full" style={{ background: 'linear-gradient(to bottom, #00f5ff, transparent)' }} />
                      <span className="font-orbitron font-bold text-xs text-white">О статье</span>
                    </div>
                    <div className="space-y-3">
                      {[
                        { icon: 'ri-time-line', label: 'Время чтения', value: `${article.readTime} мин`, color: '#00f5ff', highlight: false },
                        { icon: 'ri-calendar-line', label: 'Дата', value: formatDate(article.date), color: '#f0ff00', highlight: false },
                        { icon: 'ri-bookmark-line', label: 'Категория', value: article.category, color, highlight: false },
                        { icon: 'ri-file-list-3-line', label: 'Разделов', value: String(headings.length), color: '#39ff14', highlight: false },
                        { icon: 'ri-file-word-line', label: 'Слов', value: `~${wordCount}`, color: '#ff6b00', highlight: false },
                        { icon: 'ri-eye-line', label: 'Просмотров', value: viewCount > 0 ? formatViews(viewCount) : '—', color: '#ff2d78', highlight: true },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 rounded-lg transition-all duration-300"
                          style={item.highlight && viewCount > 0 ? { background: 'rgba(255,45,120,0.05)', padding: '4px 6px', margin: '-4px -6px', border: '1px solid rgba(255,45,120,0.12)' } : {}}
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${item.color}10`, border: `1px solid ${item.color}20` }}>
                            <i className={item.icon} style={{ color: item.color, fontSize: '13px' }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-mono text-[10px] text-white/25">{item.label}</p>
                            <p className="font-mono text-xs truncate" style={{ color: item.highlight && viewCount > 0 ? item.color : 'rgba(255,255,255,0.65)' }}>
                              {item.value}
                            </p>
                          </div>
                          {item.highlight && viewCount > 0 && (
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse"
                              style={{ background: '#ff2d78', boxShadow: '0 0 6px #ff2d78' }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Like / Dislike widget */}
                  <ArticleLikeWidget slug={article.slug} color={color} />

                  {/* Share widget */}
                  <div
                    className="rounded-2xl p-4"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(to bottom, ${color}, transparent)` }} />
                      <span className="font-orbitron font-bold text-xs text-white">Поделиться</span>
                    </div>
                    <ShareWidget url={articleUrl} title={article.title} color={color} />
                  </div>

                  {/* Top articles */}
                  <TopArticles currentSlug={slug} />

                  {/* Back to blog */}
                  <Link
                    to="/blog"
                    className="flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                    style={{ border: `1px solid ${color}30`, color }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${color}10`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <i className="ri-arrow-left-line" />
                    Все статьи блога
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* ── Related articles ── */}
        {related.length > 0 && (
          <section className="px-6 lg:px-12 pb-16" aria-label="Похожие статьи">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <h2 className="font-orbitron font-bold text-base text-white whitespace-nowrap">ЧИТАЙТЕ ТАКЖЕ</h2>
                <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map(rel => {
                  const rc = CATEGORY_COLORS[rel.category] ?? ACCENT;
                  return (
                    <Link
                      key={rel.id}
                      to={`/blog/${rel.slug}`}
                      className="group block rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                      style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${rc}12` }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${rc}35`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${rc}12`; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                    >
                      <div className="relative overflow-hidden" style={{ height: '160px' }}>
                        <img
                          src={rel.image}
                          alt={`${rel.title} — статья блога NeonPharma`}
                          title={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,10,0.6), transparent)' }} />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-0.5 rounded-full font-mono text-[10px]" style={{ background: 'rgba(5,5,10,0.75)', color: rc, border: `1px solid ${rc}30`, backdropFilter: 'blur(8px)' }}>
                            {rel.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-orbitron font-bold text-sm text-white leading-snug line-clamp-2 group-hover:opacity-80 transition-opacity mb-2">
                          {rel.title}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] text-white/30 flex items-center gap-1">
                            <i className="ri-time-line" />{rel.readTime} мин
                          </span>
                          <span className="font-mono text-[10px] flex items-center gap-1 transition-all duration-200 group-hover:gap-1.5" style={{ color: rc }}>
                            Читать <i className="ri-arrow-right-line" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="text-center mt-10">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-orbitron font-bold text-sm cursor-pointer transition-all duration-300 whitespace-nowrap"
                  style={{ border: `1px solid ${ACCENT}30`, color: ACCENT }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${ACCENT}08`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <i className="ri-article-line" />
                  Все статьи блога
                </Link>
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
};

export default ArticlePage;
